import { useState, useContext, useEffect } from "react";
import Head from "next/head";
import { useRouter } from "next/router";
import ReactMarkdown from "react-markdown";
import useSWR from "swr";
import dynamic from "next/dynamic";

import AppContext from "context/app";
import Breadcrumb from "components/global/breadcrumbs";
import RideCredit from "components/attractions/credit";
import Cover from "components/global/cover";
import Throw404 from "components/throw404";
import Attributes from "components/global/attributes";
import Gallery from "components/global/gallery";

import ATTRACTION_QUERY from "queries/attraction";

const EditLink = dynamic(() => import("components/admin/editlink"), {
  ssr: false,
});
const EditAttractionPanel = dynamic(
  () => import("components/attractions/edit"),
  { ssr: false }
);

export default function AttractionDetails() {
  const router = useRouter();
  const {
    setRecommendedRecentAttraction,
    setRecommendedRecentPark,
    setRecommendedRecentResort,
  } = useContext(AppContext);
  const [editOpen, setEditOpen] = useState(false);

  const variables = {
    slug: router.query.attraction,
  };

  const { data, error } = useSWR([ATTRACTION_QUERY, variables]);

  if (error) {
    console.log(error);
  }

  useEffect(() => {
    const attraction = data.attractions[0];
    setRecommendedRecentAttraction(attraction);

    if (attraction?.Park) {
      setRecommendedRecentPark(attraction?.Park);
    }

    if (attraction?.Park?.Resort) {
      setRecommendedRecentResort(attraction?.Park?.Resort);
    }
  }, [
    data.attractions,
    setRecommendedRecentAttraction,
    setRecommendedRecentPark,
    setRecommendedRecentResort,
  ]);

  if (!Array.isArray(data?.attractions) || !data?.attractions[0]) {
    return <Throw404 />;
  }

  const attraction = data.attractions[0];
  const park = attraction.Park;
  const description = `${attraction.Name} is an attraction located in ${park.Name}`;
  const breadcrumbs = [];

  breadcrumbs.push({
    name: park.Name,
    link: "/park/[park]",
    linkAs: `/park/${park.Slug}`,
  });

  breadcrumbs.push({
    name: attraction.Name,
    link: "/park/[park]/[attraction]",
    linkAs: `/park/${park.Slug}/${attraction.Slug}`,
  });

  let CoverPhoto = "";
  let AttractionTitle = "";
  if (attraction.CoverPhoto || attraction.MainPhoto) {
    CoverPhoto = (
      <Cover
        title={attraction.Name}
        image={attraction.CoverPhoto || attraction.MainPhoto}
      />
    );
  } else {
    AttractionTitle = (
      <h2 className="font-serif text-5xl text-gray-800">{attraction.Name}</h2>
    );
  }

  let attributes = [];

  if (Array.isArray(attraction.Manufacturers)) {
    attraction.Manufacturers.forEach((manufacturer) =>
      attributes.push({
        ValueType: "MANUFACTURER",
        Value: manufacturer.Name,
        Link: `/manufacturer/${manufacturer.Slug}`,
      })
    );
  }

  if (Array.isArray(attraction.Attribute) && attraction.Attribute.length) {
    attributes = [...attributes, ...attraction.Attribute];
  }
  return (
    <>
      <Head>
        <title>
          {attraction.Name} @ {attraction.Park.Name} | Open Coaster
        </title>
        <meta name="description" content={description} />
        <meta
          property="og:title"
          content={`${attraction.Name} @ ${park.Name}`}
        />
        <meta property="og:description" content={description} />
        <meta
          property="og:url"
          content={`https://opencoaster.com/park/${park.Slug}/${attraction.Slug}`}
        />
        {attraction.MainPhoto && (
          <>
            <meta
              property="og:image"
              content={`https://images.opencoaster.com/1200x630/${attraction.MainPhoto.hash}${attraction.MainPhoto.ext}`}
            />
            <meta
              name="twitter:image"
              content={`https://images.opencoaster.com/1200x600/${attraction.MainPhoto.hash}${attraction.MainPhoto.ext}`}
            />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta
              property="og:image:alt"
              content={
                attraction.MainPhoto.alternativeText
                  ? attraction.MainPhoto.alternativeText
                  : attraction.Name
              }
            />
            <meta name="twitter:card" content="summary_large_image" />
          </>
        )}
      </Head>
      <EditAttractionPanel
        open={editOpen}
        setOpen={setEditOpen}
        attraction={attraction}
      />
      {CoverPhoto}
      <main className="container m-6 mx-auto px-6 sm:px-0">
        <Breadcrumb items={breadcrumbs} />
        {AttractionTitle}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          <div className="order-last lg:order-first">
            {attraction.Description && (
              <div className="prose mb-3 lg:prose-xl">
                <ReactMarkdown>{attraction.Description}</ReactMarkdown>
              </div>
            )}
            <Gallery item={attraction} />
          </div>
          <div className="order-first lg:order-last">
            <ul role="list" className="space-y-3">
              {Array.isArray(attraction.Tags) && attraction.Tags.length > 0 && (
                <li className="overflow-hidden bg-white px-4 py-4 shadow sm:rounded-md sm:px-6">
                  <div className="flex flex-wrap gap-3">
                    {attraction.Tags.map((tag) => (
                      <span
                        key={`tag-${tag.Slug}`}
                        className="inline-flex items-center rounded-full bg-gray-100 px-3 py-0.5 text-sm font-medium text-gray-800"
                      >
                        {tag.Name}
                      </span>
                    ))}
                  </div>
                </li>
              )}
              <RideCredit attraction={attraction} type={attraction.Type} />
              <Attributes attributes={attributes} />
              <EditLink setEditOpen={setEditOpen} />
            </ul>
          </div>
        </div>
      </main>
    </>
  );
}
